/* eslint-disable react-hooks/rules-of-hooks */
import {
  DefaultHorizontalAlignStyle,
  Geometry2d,
  HTMLContainer,
  Rectangle2d,
  ShapeUtil,
  T,
  TLOnBeforeUpdateHandler,
  TLOnResizeHandler,
  Vec2d,
  WeakMapCache,
  toDomPrecision,
  SvgExportContext,
  stopEventPropagation,
  TLOnTranslateHandler,
} from '@tldraw/tldraw'
import Styles from './TextBoxShape.module.scss'
import cx from 'classnames'
import { TextBoxShape, TextBoxShapeProps } from './Types'
import { RichTextContent } from './RichTextContent'
import { parseRichText, reconcileRichTextForFont } from './richTextDoc'
import { RichTextEditor } from './RichTextEditor'
import { useEditableText } from './useEditableText'
import { MIN_FONT_SIZE, TEXT_PROPS } from './constants'
import {
  createTextSvgElementFromSpans,
  getCSSFontVariant,
  getFontAsBase64,
  getTextSize,
  sanitizeFontSize,
  sanitizeFontWeight,
  sanitizeText,
} from './helpers'
import {
  FontFamilyStyle,
  FontSizeStyle,
  FontWeightStyle,
  ItalicStyle,
  LetterSpacingStyle,
  LineHeightStyle,
  ShadowStyle,
  StrikethroughStyle,
  StrokeColorStyle,
  StrokeWidthStyle,
  TextBoxBackgroundFillStyle,
  TextBoxBackgroundShapeStyle,
  TextColorStyle,
  UnderlineStyle,
  UppercaseStyle,
  VerticalAlignStyle,
} from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { CSSProperties } from 'react'
import { rootStore } from 'stores/RootStore'
import { textBoxShapeMigrations } from './TextBoxShape'
import { getShadow } from './getShadow'
import { CustomEqualitySet } from 'components/VisualStudioPlayground/VisualEditor/utils/customEqualitySet'
import { SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { isEqual } from 'lodash'
import { TextManager } from 'components/VisualStudioPlayground/VisualEditor/TextManager'
import opentype from 'opentype.js'
import { reportError } from 'utils/error'
import { getBackdropColor, getBackdropShape } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import Color from 'colorjs.io'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { CustomFont } from 'stores/CustomFonts/CustomFont'

const sizeCache = new WeakMapCache<TextBoxShapeProps, { height: number; width: number }>()

export class TextBoxShapeUtil extends ShapeUtil<TextBoxShape> {
  static type = 'textBox'
  textManager = new TextManager()

  static override migrations = textBoxShapeMigrations

  static override props = {
    w: T.number,
    fontSize: FontSizeStyle,
    fontFamily: FontFamilyStyle,
    fontWeight: FontWeightStyle,
    color: TextColorStyle,
    italic: ItalicStyle,
    underline: UnderlineStyle,
    strikethrough: StrikethroughStyle,
    uppercase: UppercaseStyle,
    align: DefaultHorizontalAlignStyle,
    autoSize: T.boolean,
    text: T.string,
    lineHeight: LineHeightStyle,
    letterSpacing: LetterSpacingStyle,
    shadow: ShadowStyle,
    strokeWidth: StrokeWidthStyle,
    strokeColor: StrokeColorStyle,
    isEditable: T.boolean,
    verticalAlign: VerticalAlignStyle,
    isAutoUpdatable: T.boolean,
    backgroundFill: TextBoxBackgroundFillStyle,
    backgroundShape: TextBoxBackgroundShapeStyle,
    richText: T.nullable(T.jsonValue),
  }

  shouldDeselectAfterTranslating = false

  getDimensions(shapeProps: TextBoxShapeProps) {
    // The second argument returns the value to set if the cache misses
    return sizeCache.get(shapeProps, (props) => getTextSize(this.textManager, props))
  }

  getGeometry(shape: TextBoxShape): Geometry2d {
    return new Rectangle2d({
      ...this.getDimensions(shape.props),
      isFilled: true,
    })
  }

  getDefaultProps(): TextBoxShapeProps {
    return {
      w: 80,
      autoSize: true,
      fontSize: 16,
      fontFamily: 'Cabin',
      fontWeight: 400,
      italic: false,
      underline: false,
      strikethrough: false,
      uppercase: false,
      color: 'black',
      align: 'middle',
      text: 'Your text',
      lineHeight: 1.4,
      letterSpacing: 0,
      shadow: null,
      strokeWidth: 0,
      strokeColor: '#000000',
      isEditable: true,
      verticalAlign: 'middle',
      isAutoUpdatable: true,
      backgroundFill: null,
      backgroundShape: 'silhouetteSquare',
      richText: null,
    }
  }

  shouldDisplayResizeBox = (shape: TextBoxShape) => {
    return this.editor.isIn('select.resizing') && this.editor.getOnlySelectedShape() === shape
  }

  getNormalizedStrokeWidth = (shape: TextBoxShape) => {
    const scalingFactor = shape.props.fontSize * 0.0035
    return shape.props.strokeWidth * scalingFactor
  }

  getNormalizedShadow = (shape: TextBoxShape) => {
    if (!shape.props.shadow) return null

    const scalingFactor = shape.props.fontSize * 0.035
    return {
      x: shape.props.shadow.x * scalingFactor,
      y: shape.props.shadow.y * scalingFactor,
      blur: shape.props.shadow.blur * scalingFactor,
      color: shape.props.shadow.color,
      opacity: shape.props.shadow.opacity,
    }
  }

  isColorisable = () => true

  getBackgroundFillColor = (fill: SolidColor) => {
    if (fill.opacity >= 1) return fill.color
    return `color-mix(in srgb, ${fill.color} ${fill.opacity * 100}%, transparent)`
  }

  getCaretColor = (shape: TextBoxShape) => {
    if (getBackdropShape(this.editor, shape)?.type === 'image') {
      return '#000000'
    }

    const backdropColor = getBackdropColor(this.editor, shape)

    const white = new Color('#ffffff')
    const black = new Color('#000000')

    const whiteContrast = backdropColor.contrast(white, 'Lstar')
    const blackContrast = backdropColor.contrast(black, 'Lstar')

    return whiteContrast > blackContrast ? '#ffffff' : '#000000'
  }

  component(shape: TextBoxShape) {
    const { width, height } = this.getDimensions(shape.props)
    const {
      id,
      type,
      props: {
        text,
        color,
        fontSize,
        fontFamily,
        align,
        fontWeight,
        underline,
        strikethrough,
        italic,
        uppercase,
        lineHeight,
        letterSpacing,
        strokeWidth,
        strokeColor,
      },
    } = shape
    const { isEditing, handleInputPointerDown, handleDoubleClick } = useEditableText(this.editor, id, type)
    const shadow = this.getNormalizedShadow(shape)
    const { isMobile } = useResponsiveDesign()

    const normalizedStrokeWidth = this.getNormalizedStrokeWidth(shape)

    const style: CSSProperties = {
      ...TEXT_PROPS,
      fontWeight,
      textDecoration: [underline && 'underline', strikethrough && 'line-through'].filter(Boolean).join(' '),
      color,
      fontSize,
      fontFamily: `"${fontFamily}", "Noto Color Emoji"`,
      width: Math.max(1, width),
      height: Math.max(1, height),
      lineHeight,
      letterSpacing: `${letterSpacing / 1000}em`,
      fontVariant: getCSSFontVariant(fontFamily),
    }
    if (italic) style.fontStyle = 'italic'
    if (uppercase) style.textTransform = 'uppercase'

    let shadowStyle = shadow
      ? `${shadow.x}px ${shadow.y}px ${shadow.blur}px color-mix(in srgb, ${shadow.color} ${
          shadow.opacity * 100
        }%, transparent)`
      : undefined

    if (!strokeWidth) {
      style.textShadow = shadowStyle
    }

    // The background-fill and stroke layers must lay out IDENTICALLY to the visible text, or their
    // box/outline (and the stroke layer's shadow) drifts from it — most visibly with left-aligned
    // lists, whose marker gutter the flattened `text` string can't reproduce. So every layer renders
    // the same rich content when there is a usable richText doc, falling back to plain text otherwise.
    // `highlightClassName` is passed only by the background layer, so its silhouette highlight wraps the
    // text per line (rich text) or the plain string; the visible + stroke layers render without it.
    const renderContent = (highlightClassName?: string) => {
      if (parseRichText(shape.props.richText)) {
        return <RichTextContent richText={shape.props.richText} highlightClassName={highlightClassName} />
      }
      if (highlightClassName) {
        return <span className={highlightClassName}>{text}</span>
      }
      return text
    }

    return (
      <HTMLContainer
        id={id}
        className={Styles.textBoxContainer}
        data-align={align}
        style={{ ...style, pointerEvents: 'all' }}
      >
        {this.shouldDisplayResizeBox(shape) && (
          <div
            className={Styles.resizeOutline}
            style={{ width: toDomPrecision(width), height: toDomPrecision(height) }}
          />
        )}
        {Boolean(shape.props.backgroundFill) && (
          <div
            className={cx(Styles.textBoxBackground, Styles[shape.props.backgroundShape])}
            // The fill is consumed by CSS: the whole-box background (boxed / call to action) or the
            // per-line highlight span (silhouette). See the .textBoxBackground rules. Opacity is
            // composed into the color — the lambda multiplies backgroundFill.opacity into its fill the
            // same way, so a translucent panel previews as it ships.
            style={
              {
                '--textbox-bg':
                  shape.props.backgroundFill?.type === 'solidColor'
                    ? this.getBackgroundFillColor(shape.props.backgroundFill)
                    : undefined,
              } as CSSProperties
            }
          >
            {renderContent(Styles.silhouetteHighlight)}
          </div>
        )}
        {strokeWidth > 0 && (
          <div
            className={cx(Styles.textBox, Styles.textBoxContent)}
            style={{
              color: strokeColor,
              WebkitTextStroke: `${normalizedStrokeWidth}px ${strokeColor}`,
              textShadow: shadowStyle,
            }}
          >
            {renderContent()}
          </div>
        )}
        {isEditing ? (
          <div
            className={cx(Styles.textBox, Styles.textBoxInput)}
            onTouchEnd={stopEventPropagation}
            onContextMenu={stopEventPropagation}
            onPointerDown={handleInputPointerDown}
            onDoubleClick={handleDoubleClick}
            style={{
              height: Math.max(1, height) + lineHeight * fontSize,
              caretColor: this.getCaretColor(shape),
            }}
          >
            <RichTextEditor
              autoFocus
              richText={shape.props.richText}
              text={text}
              onChange={(richText, nextText) => {
                this.editor.updateShapes([{ id, type, props: { richText, text: nextText } }])
              }}
              handleKeyDown={(_view, event) => {
                if (event.key === 'Escape' || (event.key === 'Enter' && (event.ctrlKey || event.metaKey))) {
                  this.editor.complete()
                  return true
                }
                return false
              }}
            />
          </div>
        ) : (
          <div
            className={cx(Styles.textBox, Styles.textBoxContent)}
            onPointerDown={() => {
              if (isMobile) {
                const shapeUtil = this.editor.getShapeUtil('textBox') as TextBoxShapeUtil
                shapeUtil.shouldDeselectAfterTranslating = this.editor.getSelectedShapes().length === 0
              }
            }}
          >
            {renderContent()}
          </div>
        )}
      </HTMLContainer>
    )
  }

  override canEdit = () => true

  override canResize = () => true

  override isAspectRatioLocked = () => true

  override onResize: TLOnResizeHandler<TextBoxShape> = (shape, info) => {
    let { initialBounds, initialShape, scaleX, handle } = info

    if (info.mode === 'scale_shape' || (handle !== 'right' && handle !== 'left')) {
      const { props } = shape
      let scale = scaleX // Use the x scale for both x and y to keep the aspect ratio

      const newWidth = Math.max(1, scale * props.w)
      let newFontSize = Number.parseFloat(Math.max(MIN_FONT_SIZE, scale * props.fontSize).toFixed(1))
      if (newFontSize > 100) {
        newFontSize = Math.floor(newFontSize)
      }

      const { x, y } = info.newPoint

      return {
        id: shape.id,
        type: shape.type,
        x,
        y,
        props: {
          w: newWidth,
          fontSize: newFontSize,
        },
      }
    } else {
      const oldWidth = initialBounds.width
      const newWidth = Math.max(1, Math.abs(oldWidth * Math.max(0, scaleX)))

      const offset = new Vec2d(0, 0)

      if (handle === 'left') {
        offset.x = oldWidth - newWidth
      }

      const { x, y } = offset.rot(shape.rotation).add(initialShape)

      return {
        id: shape.id,
        type: shape.type,
        x,
        y,
        props: {
          w: newWidth,
          autoSize: false,
        },
      }
    }
  }

  indicator(shape: TextBoxShape) {
    if (this.editor.getInstanceState().isReadonly) return null
    const bounds = this.editor.getShapeGeometry(shape).bounds
    return <rect width={toDomPrecision(bounds.width)} height={toDomPrecision(bounds.height)} />
  }

  override onBeforeCreate = (shape: TextBoxShape) => {
    const font = rootStore.fonts.get(shape.props.fontFamily)
    font?.load()

    return { ...shape, props: { ...shape.props, text: sanitizeText(shape.props.text) } }
  }

  override onBeforeUpdate: TLOnBeforeUpdateHandler<TextBoxShape> = (shape, next) => {
    if (!shape.props.isEditable) {
      return { ...shape, props: { ...shape.props, isEditable: next.props.isEditable } }
    }

    // Reconcile per-run weight marks against the (possibly just-changed) font so changing family — or a
    // bold/italic shortcut on an incapable font — can't strand a run the renderer can't draw. Skip custom
    // fonts (render everything) and unknown fonts (capability unknown — don't destroy content).
    const font = rootStore.fonts.get(next.props.fontFamily)
    const richText =
      next.props.richText && font && !(font instanceof CustomFont)
        ? reconcileRichTextForFont(next.props.richText as Record<string, unknown>, font)
        : next.props.richText

    return {
      ...next,
      props: {
        ...next.props,
        fontWeight: sanitizeFontWeight(next.props.fontWeight, shape.props.fontWeight),
        fontSize: sanitizeFontSize(next.props.fontSize, shape.props.fontSize),
        text: sanitizeText(next.props.text),
        richText,
      },
    }
  }

  onTranslate: TLOnTranslateHandler<TextBoxShape> = (initialShape) => {
    if (!initialShape.props.isEditable) {
      this.editor.snaps.clear()
    }
  }

  onTranslateEnd = () => {
    if (this.shouldDeselectAfterTranslating) {
      this.editor.selectNone()
    }
    this.shouldDeselectAfterTranslating = false
  }

  override async toSvg(shape: TextBoxShape, ctx: SvgExportContext) {
    const { width, height } = this.getDimensions(shape.props)
    let text = shape.props.text
    if (shape.props.uppercase) {
      text = text.toUpperCase()
    }

    let opts = {
      fontSize: shape.props.fontSize,
      fontFamily: shape.props.fontFamily,
      fontWeight: shape.props.fontWeight.toString(),
      fontVariant: getCSSFontVariant(shape.props.fontFamily),
      textAlign: shape.props.align,
      textTransform: shape.props.uppercase ? 'uppercase' : 'none',
      textDecoration: [shape.props.underline && 'underline', shape.props.strikethrough && 'line-through']
        .filter(Boolean)
        .join(' '),
      verticalTextAlign: 'middle' as const,
      fontStyle: shape.props.italic ? 'italic' : 'normal',
      width,
      height,
      padding: 0,
      lineHeight: shape.props.lineHeight,
      overflow: 'wrap' as const,
      color: shape.props.color,
      letterSpacing: shape.props.letterSpacing,
      unitsPerEm: 1,
      ascender: shape.props.lineHeight,
    }

    let fontFace: string | null = null

    const font = rootStore.fonts.get(opts.fontFamily)
    if (font) {
      if (font instanceof CustomFont) {
        const fontAnalysis = await opentype.load(font.url)
        if (fontAnalysis) {
          opts.unitsPerEm = fontAnalysis.unitsPerEm
          opts.ascender = fontAnalysis.ascender
        } else {
          reportError('Failed to load font', {
            metaData: {
              font: font.url,
            },
          })
        }
        fontFace = await getFontAsBase64(font.url).then(
          (font) =>
            `@font-face { font-family: '${opts.fontFamily}'; src: url(data:font/woff2;base64,${font}) format('woff2'); }`,
          () => null,
        )
      } else {
        fontFace = await getFontAsBase64(font.url).then(
          (font) => `${font}`,
          () => null,
        )
        const fontAnalysis = await font.analyze({ weight: shape.props.fontWeight, italic: shape.props.italic })
        if (fontAnalysis) {
          opts.unitsPerEm = fontAnalysis.unitsPerEm
          opts.ascender = fontAnalysis.ascender
        } else {
          reportError('Failed to load google font', {
            metaData: {
              family: font.family,
              weight: shape.props.fontWeight,
              italic: shape.props.italic,
            },
          })
        }
      }
    }

    const groupEl = document.createElementNS('http://www.w3.org/2000/svg', 'g')

    if (fontFace) {
      const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style')
      styleEl.textContent = fontFace
      groupEl.insertBefore(styleEl, groupEl.firstChild)
    }

    const textBgEl = createTextSvgElementFromSpans(this.textManager.measureTextSpans(text, opts), opts)

    if (shape.props.shadow) {
      const { id, svgExportDef } = getShadow({
        id: shape.id,
        ...shape.props,
        w: width,
        h: height,
        shadow: this.getNormalizedShadow(shape),
      })
      const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter')
      filter.setAttribute('id', id)
      ctx.addExportDef(svgExportDef)
      groupEl.setAttribute('filter', `url(#${id})`)
    }

    if (shape.props.strokeWidth > 0) {
      const optsWithStroke = {
        ...opts,
        strokeColor: shape.props.strokeColor,
        strokeWidth: this.getNormalizedStrokeWidth(shape),
      }
      const textBgElWithStroke = createTextSvgElementFromSpans(
        this.textManager.measureTextSpans(text, optsWithStroke),
        optsWithStroke,
      )
      groupEl.append(textBgElWithStroke)
    }

    groupEl.append(textBgEl)

    return groupEl
  }

  getColors = (shape: TextBoxShape) => {
    const colors = new CustomEqualitySet<SolidColor>(isEqual)
    const { color, strokeColor, strokeWidth } = shape.props

    colors.add({ type: 'solidColor', color, opacity: 1 })

    if (strokeWidth > 0) {
      colors.add({ type: 'solidColor', color: strokeColor, opacity: 1 })
    }

    return colors
  }

  hideRotateHandle = () => true
}
